import { type JSONData } from "../../base_classes/dto.d";
import { AttributeOptionSet } from "../attribute_option_set";
import AttributeValidators from "./custom_validators";
import { AttributeDto, AttributeType } from "./dto.gen";
import { type IAttribute } from "./entity.d";
import AttributeParsers from "./parsers";

export class Attribute extends AttributeDto implements IAttribute {
  constructor(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        },
    data?: JSONData,
  ) {
    super(data, AttributeParsers, AttributeValidators);

    if (data !== undefined) {
      // Set data
      this.set(data);
    }

    this.buildPathObject(pathVars);
  }
  /// ///////////////////////////////////////
  /// //////// PRIVATE VARIABLES ////////////
  /// ///////////////////////////////////////

  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////
  /**
   * The selected option sets.
   */
  selectedOptionSets: AttributeOptionSet[] = [];

  /**
   * The selected options.
   */
  selectedOptions: string[] = [];

  /**
   * All options
   */
  get allOptions(): {
    label: string;
    id: string;
  }[] {
    return this.optionSets.flatMap((optionSet) =>
      optionSet.options.map((option) => ({
        label: option.value,
        id: option.id.toString(),
      })),
    );
  }

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  public async get(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        }
      | undefined,
  ): Promise<Attribute> {
    return (await super.get(pathVars)) as Attribute;
  }

  getSelectedOptions(): {
    label: string;
    id: string;
  }[] {
    return this.optionSets
      .flatMap((optionSet) => optionSet.options)
      .filter((option) => this.selectedOptions.includes(option.id.toString()))
      .map((option) => ({
        label: option.value,
        id: option.id.toString(),
      }));
  }

  formatAsInputAttribute(): {
    id: string;
    label: string;
    unitAttribute: boolean;
    values: {
      id: string;
      label: string;
    }[];
  } {
    return {
      id: this.id,
      label: this.name,
      unitAttribute: this.type === AttributeType.UNITS,
      values: this.getSelectedOptions(),
    };
  }

  toggleOptionSet(optionSetId: string): void {
    if (this.selectedOptionSets.some((os) => os.id === optionSetId)) {
      this.selectOptionsByOptionSetId(optionSetId);
    } else {
      this.unselectOptionsByOptionSetId(optionSetId);
    }
  }

  selectOptionsByOptionSetId(optionSetId: string): void {
    const optionSet = this.optionSets.find((os) => os.id === optionSetId);
    if (optionSet) {
      optionSet.options
        .map((option) => option.id.toString())
        .forEach((id) => {
          if (!this.selectedOptions.includes(id)) {
            this.selectedOptions.push(id);
          }
        });
    }
  }

  unselectOptionsByOptionSetId(optionSetId: string): void {
    const optionSet = this.optionSets.find((os) => os.id === optionSetId);
    if (optionSet) {
      optionSet.options
        .map((option) => option.id.toString())
        .forEach((id) => {
          this.selectedOptions = this.selectedOptions.filter(
            (selectedId) => selectedId !== id,
          );
        });
    }
  }

  /// ///////////////////////////////////////
  /// /////////// STATIC METHODS ////////////
  /// ///////////////////////////////////////

  static override FromJSON(data?: JSONData): Attribute {
    return new Attribute(undefined, data);
  }

  static override FromJSONArray(data: JSONData[]): Attribute[] {
    return data.map((item) => Attribute.FromJSON(item));
  }

  static FromDto(dto: AttributeDto): Attribute {
    return new Attribute(undefined, dto.toJSON());
  }
}
